import { type AxiosResponse } from 'axios'
import type { AxiosOptions } from 'shared/api'
import { baseApi } from 'entities/Auth'
import {
  type IResponseOrganization,
  type IParamsOrganizationMessagingRegion,
  type IResponseMultiOrganizationsList,
  type IResponseUpdatePhoneCheckerEnable,
  type IParamsUpdatePhoneCheckerEnable,
  type IResponseUpdateFallbackTeamId,
  type IResponseUpdateOrganizationAutoCloseSetting,
  type IParamsUpdateOrganizationAutoCloseSetting,
  type IParamsUpdateOrganizationAutoUnsubscribe,
  type IResponseUpdateOrganizationAutoUnsubscribe,
  type IParamsUpdateOrganizationGlobalConversationHistory,
  type IResponseUpdateOrganizationGlobalConversationHistory,
  type IResponseUpdateOrganizationDoubleOptIn,
  type IParamsUpdateOrganizationDoubleOptIn,
  type IResponseUpdateOrganizationExtendedInternationalTexting,
  type IParamsUpdateOrganizationExtendedInternationalTexting,
  type IResponseUpdateOrganizationAutoCloseDays,
  type IParamsUpdateOrganizationAutoCloseDays,
  type IParamsUpdateOrganizationOutboundCalls,
  type IResponseUpdateOrganizationOutboundCalls,
  type IParamsUpdateOrganizationOutboundCallsAircall,
  type IResponseUpdateOrganizationOutboundCallsAircall,
  type IResponseUpdateOrganizationRecordAutomatically,
  type IParamsUpdateOrganizationOutboundRecordAutomatically,
  type IParamsUpdateOrganizationRequireTwoStepAuth,
  type IResponseUpdateOrganizationRequireTwoStepAuth,
  type IResponseDisableOrganizationPhoneCheckr,
  type IParamsEnableOrganizationPhoneCheckr,
  type IResponseEnableOrganizationPhoneCheckr,
  type IParamsUpdateAutoAssignContactOwner,
  type IResponseUpdateAutoAssignContactOwner,
  type IParamsUpdateAutoCreateConversations,
  type IResponseUpdateAutoCreateConversations,
  type IParamsUpdateOrganizationAnyFormatOptOut,
  type IResponseUpdateOrganizationAnyFormatOptOut,
  type IParamsOrganizationName,
  type IParamsTrackOrganizationActivity,
  type IResponseOrganizationEventTypes,
  type IOrganizationExportParams,
  type IParamsOrganizationQuietHours,
  type IResponseOrganizationAnswers,
} from 'entities/Organization/api/types'
import { type IResponseNumber } from 'entities/Phone/api/types'

class Api {
  getOrganizationCurrent(): Promise<AxiosResponse<IResponseOrganization>> {
    return baseApi.get('organization/current')
  }

  updateOrganizationMessagingRegion(
    params: IParamsOrganizationMessagingRegion
  ): Promise<AxiosResponse<IResponseOrganization>> {
    return baseApi.post('organization/update-messaging-canada-only', params)
  }

  getMultiOrganizationsList(): Promise<AxiosResponse<IResponseMultiOrganizationsList>> {
    return baseApi.get('organization/multiorganizations/list', {
      params: { sortOrder: 'asc' },
    })
  }

  updatePhoneCheckerEnable(
    params: IParamsUpdatePhoneCheckerEnable
  ): Promise<AxiosResponse<IResponseUpdatePhoneCheckerEnable>> {
    return baseApi.post('organization/phone-checker/enable', params)
  }

  updateFallbackTeamId(
    fallback_teamId: number
  ): Promise<AxiosResponse<IResponseUpdateFallbackTeamId>> {
    return baseApi.post(`organization/set-fallback-inbox/${fallback_teamId}`, {
      fallback_team: fallback_teamId,
    })
  }

  getOrganizationCnamFreeNumbers(): Promise<AxiosResponse<IResponseNumber[]>> {
    return baseApi.get('organization/cnam/free-numbers')
  }

  updateOrganizationAutoCloseDays(
    params: IParamsUpdateOrganizationAutoCloseDays
  ): Promise<AxiosResponse<IResponseUpdateOrganizationAutoCloseDays[]>> {
    return baseApi.post('organization/auto-close-days', params)
  }

  updateOrganizationAutoCloseSetting(
    params: IParamsUpdateOrganizationAutoCloseSetting
  ): Promise<AxiosResponse<IResponseUpdateOrganizationAutoCloseSetting[]>> {
    return baseApi.post('organization/auto-close-setting', params)
  }

  updateOrganizationAutoCloseUnsubscribe(
    params: IParamsUpdateOrganizationAutoUnsubscribe
  ): Promise<AxiosResponse<IResponseUpdateOrganizationAutoUnsubscribe[]>> {
    return baseApi.post('organization/auto-close-unsubscribe', params)
  }

  updateOrganizationGlobalConversationHistory(
    params: IParamsUpdateOrganizationGlobalConversationHistory
  ): Promise<AxiosResponse<IResponseUpdateOrganizationGlobalConversationHistory>> {
    return baseApi.post('organization/conversation-history-settings', params)
  }

  updateOrganizationDoubleOptIn(
    params: IParamsUpdateOrganizationDoubleOptIn
  ): Promise<AxiosResponse<IResponseUpdateOrganizationDoubleOptIn>> {
    return baseApi.put('organization/double-opt-in', params)
  }

  updateOrganizationExtendedInternationalTexting(
    params: IParamsUpdateOrganizationExtendedInternationalTexting
  ): Promise<AxiosResponse<IResponseUpdateOrganizationExtendedInternationalTexting>> {
    return baseApi.post('organization/extended-international-texting-settings', params)
  }

  updateOrganizationOutboundInbondCalls(
    params: IParamsUpdateOrganizationOutboundCalls
  ): Promise<AxiosResponse<IResponseUpdateOrganizationOutboundCalls[]>> {
    return baseApi.post('organization/call-status', params)
  }

  updateOrganizationOutboundCallsAircall(
    params: IParamsUpdateOrganizationOutboundCallsAircall
  ): Promise<AxiosResponse<IResponseUpdateOrganizationOutboundCallsAircall>> {
    return baseApi.post('organization/call-settings/call-via-aircall', params)
  }

  updateOrganizationRecordAutomatically(
    params: IParamsUpdateOrganizationOutboundRecordAutomatically
  ): Promise<AxiosResponse<IResponseUpdateOrganizationRecordAutomatically>> {
    return baseApi.post('organization/call-settings/call-auto-record', params)
  }

  updateOrganizationRequireTwoStepAuth(
    params: IParamsUpdateOrganizationRequireTwoStepAuth
  ): Promise<AxiosResponse<IResponseUpdateOrganizationRequireTwoStepAuth>> {
    return baseApi.post('2fa/organization', params)
  }

  enableOrganizationPhoneCheckr(
    params: IParamsEnableOrganizationPhoneCheckr
  ): Promise<AxiosResponse<IResponseEnableOrganizationPhoneCheckr>> {
    return baseApi.post('organization/phone-checker/enable', params)
  }

  disableOrganizationPhoneCheckr(): Promise<
    AxiosResponse<IResponseDisableOrganizationPhoneCheckr>
  > {
    return baseApi.post('organization/phone-checker/disable')
  }

  updateAutoAssignContactOwner(
    params: IParamsUpdateAutoAssignContactOwner
  ): Promise<AxiosResponse<IResponseUpdateAutoAssignContactOwner>> {
    return baseApi.post('organization/auto-assign-contact-owner', params)
  }

  updateAutoCreateConversations(
    params: IParamsUpdateAutoCreateConversations
  ): Promise<AxiosResponse<IResponseUpdateAutoCreateConversations>> {
    return baseApi.post('organization/call-settings/create-new-conv-via-aircall', params)
  }

  updateOrganizationAnyFormatOptOut(
    params: IParamsUpdateOrganizationAnyFormatOptOut
  ): Promise<AxiosResponse<IResponseUpdateOrganizationAnyFormatOptOut>> {
    return baseApi.post('organization/any-format-opt-out', params)
  }

  updateOrganizationName(data: IParamsOrganizationName): Promise<AxiosResponse> {
    return baseApi.post('organization/profile', data)
  }

  purgePhi(): Promise<AxiosResponse> {
    return baseApi.post('organization/purge-phi-data')
  }

  trackOrganizationActivity(data: IParamsTrackOrganizationActivity): Promise<AxiosResponse> {
    return baseApi.post('activity-logs/api/v1/track/activity', data)
  }

  getOrganizationExport = (
    params: IOrganizationExportParams,
    options?: AxiosOptions
  ): Promise<AxiosResponse> => {
    return baseApi.get('activity-logs/api/v1/export', {
      params,
      ...options,
    })
  }

  getOrganizationEventTypes = (): Promise<AxiosResponse<IResponseOrganizationEventTypes>> => {
    return baseApi.get('activity-logs/api/v1/event-types')
  }

  getOrganizationAnswers = (): Promise<AxiosResponse<IResponseOrganizationAnswers>> => {
    return baseApi.get('organization/answers')
  }

  updateOrganizationQuietHours(
    params: IParamsOrganizationQuietHours
  ): Promise<AxiosResponse<IParamsOrganizationQuietHours>> {
    return baseApi.post('organization/quiet-hours-settings', params)
  }
}

export const OrganizationApi = new Api()
